import React, { useState, useEffect, useRef } from 'react';
import { LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const LOGO_URL = '/brand/twp-logo.png';
const FONT_SANS = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const FONT_DISPLAY = "'Fraunces', Georgia, serif";

export default function ShopTopBar() {
  const { currentUser, signOut } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const displayName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Shop Worker';

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowMenu(false);
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const handleLogout = async () => {
    try {
      setShowMenu(false);
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
      alert('Failed to sign out. Please try again.');
    }
  };

  return (
    <div style={{
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      height: '70px'
    }}>
      <header style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        position: 'relative',
        overflow: 'visible',
        fontFamily: FONT_SANS
      }}>
        {/* Background wrapper with overflow hidden */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          zIndex: 1
        }}>
          {/* Blurred Background */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/brand/shop-bg.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(8px)'
          }} />

          {/* Dark Overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(139, 69, 19, 0.75) 100%)'
          }} />
        </div>

        {/* Logo & Title */}
        <div style={{
          position: 'relative',
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            background: 'white',
            borderRadius: '12px',
            padding: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img
              src={LOGO_URL}
              alt="The Window Place"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </div>

          <div>
            <div style={{
              fontFamily: FONT_DISPLAY,
              fontSize: '17px',
              fontWeight: '600',
              color: '#ffffff',
              textShadow: '0 2px 6px rgba(0, 0, 0, 0.8)',
              letterSpacing: '0'
            }}>
              Shop Production
            </div>
            <div style={{
              fontSize: '11px',
              color: 'rgba(255, 255, 255, 0.95)',
              textShadow: '0 2px 6px rgba(0, 0, 0, 0.8)',
              fontWeight: '600',
              letterSpacing: '0.2px'
            }}>
              The Window Place LLC
            </div>
          </div>
        </div>

        {/* User Menu */}
        <div style={{ 
          position: 'relative',
          zIndex: 2010
        }}>
          <button
            ref={buttonRef}
            onClick={() => setShowMenu(!showMenu)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 16px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              transition: 'all 0.2s',
              minHeight: '44px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: '700',
              color: 'white',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)'
            }}>
              {displayName.charAt(0).toUpperCase()}
            </div>
            <span style={{ 
              textShadow: '0 2px 6px rgba(0, 0, 0, 0.8)',
              letterSpacing: '0.2px'
            }}>
              {displayName}
            </span>
          </button>

          {showMenu && (
            <div 
              ref={menuRef}
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                minWidth: '220px',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                zIndex: 2011
              }}
            >
              <div style={{
                padding: '16px',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#2b2b2b',
                  marginBottom: '4px'
                }}>
                  {displayName}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#999'
                }}>
                  {currentUser?.email}
                </div>
              </div>

              <button
                onClick={handleLogout}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#dc2626',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fef2f2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <LogOut size={15} strokeWidth={2.25} />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </header>
    </div>
  );
}